import React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { LayoutDashboard, Home as HomeIcon, Users, CalendarClock, Inbox, Send, Phone, Radio, Shield, Clock, UserCheck, MessageSquare, Share2, BookText, PlugZap } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import useInboxUnread from '../../hooks/useInboxUnread';

const NAV = [
  { to: '/crm', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/crm/listings', label: 'Listings', icon: HomeIcon },
  { to: '/crm/clients', label: 'Clients', icon: Users },
  { to: '/crm/appointments', label: 'Appointments', icon: CalendarClock },
  { to: '/crm/inbox', label: 'Inbox', icon: Inbox, key: 'inbox' },
  { to: '/crm/blasts', label: 'Blasts', icon: Send },
  { to: '/crm/live-calls', label: 'Live Calls', icon: Radio },
  { to: '/crm/settings/voice', label: 'AI Voice', icon: Phone },
  { to: '/crm/settings/text-messaging', label: 'Text Messaging', icon: MessageSquare },
  { to: '/crm/settings/social-media', label: 'Social Media', icon: Share2 },
  { to: '/crm/settings/booking', label: 'Booking', icon: Clock },
  { to: '/crm/settings/professionals', label: 'Referrals', icon: UserCheck },
  { to: '/crm/admin/voice-numbers', label: 'Admin', icon: Shield, adminOnly: true },
  { to: '/crm/admin/ai-scripts', label: 'AI Scripts', icon: BookText, adminOnly: true },
  { to: '/crm/admin/meta', label: 'Meta', icon: PlugZap, adminOnly: true },
];

export default function CrmLayout() {
  const { user } = useAuth();
  const { count: unread } = useInboxUnread({ user });
  // Admin-only nav items are stripped for regular users so the tab isn't
  // even a hint that the route exists. The route itself is still guarded
  // server-side (see `_require_admin` in `admin_voice_numbers.py`), so
  // this is a UX filter, not a security boundary.
  const visibleNav = NAV.filter((item) => !item.adminOnly || user?.role === 'admin');
  return (
    <div className="min-h-screen bg-brand-cream/40" data-testid="crm-shell">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
        <div className="mb-6">
          <h1 className="font-serif text-3xl sm:text-4xl text-brand-green">CRM</h1>
          <p className="text-sm text-brand-green/70 mt-1">
            Your shared workspace — synced with the LISTING AiGENT mobile app.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6">
          <aside className="md:sticky md:top-24 self-start">
            <nav className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible bg-white rounded-xl p-2 shadow-sm">
              {visibleNav.map((item) => {
                const Icon = item.icon;
                const showBadge = item.key === 'inbox' && unread > 0;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    data-testid={`crm-nav-${item.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                        isActive
                          ? 'bg-brand-green text-brand-cream'
                          : 'text-brand-green/80 hover:bg-brand-cream'
                      }`
                    }
                  >
                    <Icon className="w-4 h-4" />
                    <span className="flex-1">{item.label}</span>
                    {showBadge && (
                      <span data-testid="crm-inbox-unread-badge"
                            className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-brand-gold text-brand-green">
                        {unread > 99 ? '99+' : unread}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </nav>
          </aside>

          <section className="min-w-0" data-testid="crm-content">
            <Outlet />
          </section>
        </div>
      </div>
    </div>
  );
}
